import { groupBy } from 'lodash-es';
import type { flightEntity } from 'freedom-shared';

import { makeDaysOW } from './group-direct-flights-ow';
import { makeDaysRT } from './group-direct-flights-rt';
import type { DirectFlightGroup } from '../types';

/**
 * Checks if an offer has the same airline for both flights (RT only).
 * This is required for RT flights to ensure consistency.
 */
export function hasSameAirlineForRT(
  offer: flightEntity.types.MappedOffer,
): boolean {
  return (
    offer.flights[0]?.segments[0]?.airline ===
    offer.flights[1]?.segments[0]?.airline
  );
}

function createAirlineOffersGroup(
  airlineIata: string,
  airlineTitle: string,
  airlineOffers: flightEntity.types.MappedOffer[],
  isRT: boolean,
): DirectFlightGroup {
  const minPrice = Math.min(
    ...airlineOffers.map(offer => Number(offer.price.amount)),
  );

  const baseGroup = {
    airline: {
      iata: airlineIata,
      title: airlineTitle,
    },
    minPrice,
    offers: airlineOffers,
  };

  if (isRT) {
    const { days, backwardTimesByForwardTime, forwardTimesByBackwardTime } =
      makeDaysRT(airlineOffers);
    return {
      ...baseGroup,
      days,
      backwardTimesByForwardTime,
      forwardTimesByBackwardTime,
    };
  }

  const days = makeDaysOW(airlineOffers);
  return {
    ...baseGroup,
    days,
  };
}

/**
 * Groups direct flights by airline, then by departure day (for RT) or all times together (for OW).
 *
 * For RT flights, only offers where the same airline operates both directions are included.
 * The result is sorted by minimum price (ascending).
 */
export function groupDirectFlights(
  directOffers: flightEntity.types.MappedOffer[],
  isRT: boolean,
): DirectFlightGroup[] {
  // For RT, filter to keep only offers where the same airline operates both directions
  const filteredOffers = isRT
    ? directOffers.filter(hasSameAirlineForRT)
    : directOffers;

  if (filteredOffers.length === 0) {
    return [];
  }

  // Group by airline IATA code
  const airlineGroups = groupBy(
    filteredOffers,
    offer => offer.flights[0].segments[0].airline,
  );

  // Create groups for each airline
  const result: DirectFlightGroup[] = Object.entries(airlineGroups).map(
    ([airlineIata, airlineOffers]) => {
      const { airlineTitle } = airlineOffers[0].flights[0].segments[0];
      return createAirlineOffersGroup(
        airlineIata,
        airlineTitle,
        airlineOffers,
        isRT,
      );
    },
  );

  // Sort groups by minimum price (ascending)
  return result.sort((a, b) => a.minPrice - b.minPrice);
}
